import { AuthenticationService } from 'src/app/core/services/auth.service';
import { ClockService, ClockZoneDefinition, ClockZoneTick } from 'src/app/core/services/utelize/general/clock-service';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ConfigSystemParams } from 'src/app/core/consts';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { Subscription } from 'rxjs';

@Component({
  selector   : 'app-clock-chip-group',
  templateUrl: './clock-chip-group.component.html',
  styleUrls  : ['./clock-chip-group.component.scss']
})
export class ClockChipGroupComponent implements OnDestroy, OnInit {

  @Input() dropdownContainer    : 'body' | null='body';
  @Input() editable             : boolean=false;
  @Input() hideFlags            : boolean=false;
  @Input() showBarOptionInPicker: boolean=true;
  @Input() zoneKeys?            : string[];

  @Output() showBarRequested: EventEmitter<void>=new EventEmitter<void>();

  private ticksSub: Subscription | undefined;

  public ticks     : { [key: string]: ClockZoneTick | undefined }={             };
  public zoneChecks: { [key: string]: boolean                   }={ local: true };

  constructor(
    private authenticationService: AuthenticationService,
    private clockService         : ClockService,
    private configSystemService  : ConfigSystemService,
    private exceptionsService    : ExceptionsService
  ) { }

  public ngOnInit(): void {
    try {
      const curUser=this.authenticationService.currentUserObject();
      this.clockService.ensureStarted(curUser);

      if (this.editable && !this.zoneKeys)
        this.zoneChecks=this.clockService.loadSelectedZoneKeys(curUser);

      this.ticksSub=this.clockService.ticks$.subscribe(next => {
        this.ticks=next;
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy(): void {
    try {
      this.ticksSub?.unsubscribe();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public get allZones(): ReadonlyArray<ClockZoneDefinition> {
    try {
      return this.clockService.zoneDefinitions;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  public get allZonesChecked(): boolean {
    try {
      return this.clockService.zoneDefinitions.every(z => this.zoneChecks[z.key]);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public get localTimezoneLabel(): string {
    try {
      return this.clockService.localTimezoneLabel;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '';
    }
  }

  public get selectedZones(): ReadonlyArray<ClockZoneDefinition> {
    try {
      const all=this.clockService.zoneDefinitions;

      if (this.zoneKeys && this.zoneKeys.length>0) {
        const fixed=this.zoneKeys
                    .map(k => all.find(z => z.key===k))
                    .filter((z): z is ClockZoneDefinition => !!z);

        return fixed.length>0 ? fixed : [all[0]];
      }

      const selected=all.filter(z => this.zoneChecks[z.key]);

      if (selected.length<=1)
        return selected.length>0 ? selected : [all[0]];

      return [...selected].sort((a, b) => this.clockService.timezoneOffsetMinutes(a.timezone)-this.clockService.timezoneOffsetMinutes(b.timezone));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  public get showTooltips(): boolean {
    try {
      return this.configSystemService.getValueAsBoolean(ConfigSystemParams.ShowTooltips, false);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public compactTime(key: string, isLast: boolean): string {
    try {
      const time=this.ticks[key]?.iconTime ?? '';

      if (isLast)
        return time;

      const parts=time.split(':');
      return parts.length>=2 ? `${parts[0]}:${parts[1]}` : time;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '';
    }
  }

  public isZoneChecked(key: string): boolean {
    try {
      return !!this.zoneChecks[key];
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public showBarClicked(): void {
    try {
      this.clockService.setBarDismissed(this.authenticationService.currentUserObject(), false);
      this.showBarRequested.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public toggleAllZones(): void {
    try {
      const all=this.clockService.zoneDefinitions;

      if (this.allZonesChecked) {
        all.forEach(z => this.zoneChecks[z.key]=(z.key==='local'));
      } else {
        all.forEach(z => this.zoneChecks[z.key]=true);
      }

      this.persist();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public toggleZoneCheck(key: string, event?: Event): void {
    try {
      event?.stopPropagation();
      this.zoneChecks[key]=!this.zoneChecks[key];
      this.persist();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private persist(): void {
    try {
      this.clockService.saveSelectedZoneKeys(this.authenticationService.currentUserObject(), this.zoneChecks);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
